import { useState, useEffect, useRef } from 'react';
import { Download } from 'lucide-react';
import { useAuth } from '../state/useAuth';
import { useData } from '../context/DataContext';
import WorkerPlanning from '../pages/worker/tabs/WorkerPlanning';
import WorkerChantier from '../pages/worker/tabs/WorkerChantier';
import WorkerDocuments from '../pages/worker/tabs/WorkerDocuments';
import WorkerProfil from '../pages/worker/tabs/WorkerProfil';
import './WorkerLayout.css';

const TABS = [
  {
    id: 'planning',
    label: 'Planning',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    )
  },
  {
    id: 'chantier',
    label: 'Chantier',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    )
  },
  {
    id: 'documents',
    label: 'Documents',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" />
      </svg>
    )
  },
  {
    id: 'profil',
    label: 'Profil',
    icon: (active) => (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={active ? 'var(--w-accent)' : '#94a3b8'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
      </svg>
    )
  }
];

export default function WorkerLayout() {
  const { model: user } = useAuth();
  const { assignments, loading, refresh } = useData();
  const [activeTab, setActiveTab] = useState('planning');
  const [selectedChantierId, setSelectedChantierId] = useState(null);
  const contentRef = useRef(null);

  // Scroll to top fluide au changement d'onglet
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  // Logique PWA Global (Bouton d'en-tête discret)
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled] = useState(window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    } else {
      alert("Sur iPhone/iPad : Appuyez sur le bouton central 'Partager' en bas de l'écran, puis choisissez 'Sur l'écran d'accueil'.");
    }
  };

  // Sync activeChantier from real-time assignments
  const activeChantier = assignments.find(a => {
    const isTarget = a.chantier === selectedChantierId || a.expand?.chantier?.id === selectedChantierId;
    if (user?.can_view_all_planning) return isTarget;
    const isOwner = Array.isArray(a.user) ? a.user.includes(user?.id) : a.user === user?.id;
    return isTarget && isOwner;
  })?.expand?.chantier || (assignments.find(a => a.expand?.chantier?.id === selectedChantierId)?.expand?.chantier);

  const activeAssignment = assignments.find(a => {
    const isTarget = a.chantier === selectedChantierId || a.expand?.chantier?.id === selectedChantierId;
    if (user?.can_view_all_planning) return isTarget;
    const isOwner = Array.isArray(a.user) ? a.user.includes(user?.id) : a.user === user?.id;
    return isTarget && isOwner;
  });

  // Consolidate current chantier data
  const currentChantierData = activeChantier ? {
    ...activeChantier,
    assignmentNotes: activeAssignment?.notes,
    assignmentId: activeAssignment?.id,
    assignmentStart: activeAssignment?.start_date,
    assignmentEnd: activeAssignment?.end_date,
    assignmentHours: activeAssignment?.hours_override
  } : null;

  // Auto-select today's mission if none selected
  useEffect(() => {
    if (!selectedChantierId && !loading && assignments && user) {
      const today = new Date().toDateString();
      const todayAssignment = assignments.find(a => {
        const isOwner = Array.isArray(a.user) ? a.user.includes(user.id) : a.user === user.id;
        const validUser = user.can_view_all_planning || isOwner;
        return validUser && new Date(a.start_date).toDateString() === today;
      });
      if (todayAssignment) setSelectedChantierId(todayAssignment.chantier);
    }
  }, [assignments, loading, user, selectedChantierId]);

  // Listen to external open events (from Dashboard)
  useEffect(() => {
    const handleOpen = (e) => {
      const mission = e.detail;
      const chantierId = mission.chantier || mission.expand?.chantier?.id;
      if (chantierId) {
        setSelectedChantierId(chantierId);
        setActiveTab('chantier');
      }
    };
    window.addEventListener('open-mission', handleOpen);
    return () => window.removeEventListener('open-mission', handleOpen);
  }, []);

  const openChantier = (chantier) => {
    setSelectedChantierId(chantier.id || chantier);
    setActiveTab('chantier');
  };

  return (
    <div className="worker-layout">
      <header className="worker-header">
        <div className="w-header-left">
          <span className="w-header-name">Bonjour, {user?.name?.split(' ')[0]}</span>
        </div>
        <div className="w-header-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!isInstalled && (
            <button 
              onClick={handleInstallClick}
              title="Installer l'application"
              style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--w-accent)' }}
            >
              <Download size={16} strokeWidth={2.5} />
            </button>
          )}
          <div className="w-live-indicator">
            <span className="w-pulse" />
            <span className="w-live-text">DIRECT</span>
          </div>
        </div>
      </header>

      <div className="worker-content" ref={contentRef}>
        {loading && activeTab === 'planning' ? (
          <div className="w-loading-overlay">Chargement...</div>
        ) : (
          <>
            {activeTab === 'planning' && <WorkerPlanning onOpenChantier={openChantier} />}
            {activeTab === 'chantier' && <WorkerChantier chantier={currentChantierData} onClose={() => setSelectedChantierId(null)} />}
            {activeTab === 'documents' && <WorkerDocuments />}
            {activeTab === 'profil' && <WorkerProfil />}
          </>
        )}
      </div>

      <nav className="worker-bottom-nav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`w-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            aria-label={tab.label}
          >
            {tab.icon(activeTab === tab.id)}
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
